/**
 * Chip — interactive token.
 *
 * Represents one piece of selectable / removable content: a filter
 * facet ("Color: red"), a tag in an input, a quick-action affordance,
 * a category mark. The whole pill is a clickable surface, with an
 * optional trailing close button for "tag-in-input" use cases.
 *
 * Distinction from neighbours:
 *   - `<Badge>` is INFORMATIONAL and not interactive.
 *   - `<Button color="primary">` is action-led at higher visual weight.
 *     Chips are quieter and used in bulk.
 *
 * Sizes (≥24 px height — thumb-friendly):
 *   - `s` — 24 px tall, `label.s`. Compact filter rows.
 *   - `m` — 32 px tall, `label.m`. Default.
 *   - `l` — 40 px tall, `label.l`. Quick-action on spacious surfaces.
 *
 * Variants mirror Badge — `solid` / `soft` / `outline`.
 *
 * @format
 */

import {forwardRef, isValidElement, type MouseEvent, type ReactNode} from 'react';

import {Box} from '@mui/material';

import {styled} from '@mui/material/styles';

import {text, iconPxForRole, type TypographyRole} from 'ui/theme/typography';

import {Icon} from '../Icon';

export const CHIP_SIZES = ['xs', 's', 'sm', 'm'] as const;
export type ChipSize = (typeof CHIP_SIZES)[number];

export const CHIP_TONES = ['primary', 'good', 'bad', 'alert', 'neutral'] as const;
export type ChipTone = (typeof CHIP_TONES)[number];

export const CHIP_VARIANTS = ['solid', 'soft', 'outline'] as const;
export type ChipVariant = (typeof CHIP_VARIANTS)[number];

interface SizeGeometry {
  height: number;
  padX: number;
  padXIcon: number;
  gap: number;
  iconPx: number;
  removePx: number;
  labelRole: TypographyRole;
}

const GEO: Record<ChipSize, SizeGeometry> = {
  xs: {height: 24, padX: 10, padXIcon: 6, gap: 4, iconPx: 14, removePx: 12, labelRole: 'label.s'},
  s: {height: 32, padX: 12, padXIcon: 8, gap: 6, iconPx: 16, removePx: 14, labelRole: 'label.m'},
  sm: {height: 36, padX: 14, padXIcon: 10, gap: 7, iconPx: 18, removePx: 15, labelRole: 'label.m'},
  m: {height: 48, padX: 20, padXIcon: 14, gap: 10, iconPx: 22, removePx: 18, labelRole: 'label.l'},
};

interface ChipPalette {
  bg: string;
  bgHover: string;
  fg: string;
  border: string;
}

function paletteFor(
  theme: import('@mui/material/styles').Theme,
  variant: ChipVariant,
  tone: ChipTone,
  selected: boolean,
): ChipPalette {
  const p = theme.palette;

  if (selected) {
    if (tone === 'neutral') {
      return {
        bg: p.surface.surface3,
        bgHover: p.surface.surface3,
        fg: p.text.primary,
        border: 'transparent',
      };
    }
    const role = p[tone];
    return {
      bg: role.main,
      bgHover: role.hover,
      fg: role.contrastText,
      border: 'transparent',
    };
  }

  if (tone === 'neutral') {
    if (variant === 'solid') {
      return {
        bg: p.surface.surface2,
        bgHover: p.surface.surface3,
        fg: p.text.primary,
        border: 'transparent',
      };
    }
    if (variant === 'soft') {
      return {
        bg: p.surface.surface1,
        bgHover: p.surface.surface2,
        fg: p.surface.onSurfaceLight,
        border: 'transparent',
      };
    }
    return {
      bg: 'transparent',
      bgHover: p.surface.surface1,
      fg: p.surface.onSurfaceLight,
      border: p.surface.onSurfaceMuted,
    };
  }

  const role = p[tone];
  if (variant === 'solid') {
    return {
      bg: role.main,
      bgHover: role.hover,
      fg: role.contrastText,
      border: 'transparent',
    };
  }
  if (variant === 'soft') {
    return {
      bg: role.container,
      bgHover: role.hoverContainer,
      fg: role.onContainer,
      border: 'transparent',
    };
  }
  return {
    bg: 'transparent',
    bgHover: role.container,
    fg: role.main,
    border: role.main,
  };
}

const Root = styled('button', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $size: ChipSize;
  $variant: ChipVariant;
  $tone: ChipTone;
  $selected: boolean;
  $clickable: boolean;
  $hasRemove: boolean;
  $hasLeftIcon: boolean;
}>(({theme, $size, $variant, $tone, $selected, $clickable, $hasRemove, $hasLeftIcon}) => {
  const g = GEO[$size];
  const p = paletteFor(theme, $variant, $tone, $selected);
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: g.gap,
    height: g.height,
    paddingLeft: $hasLeftIcon ? g.padXIcon : g.padX,
    paddingRight: $hasRemove ? g.padXIcon : g.padX,
    borderRadius: g.height / 2,
    boxSizing: 'border-box',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    cursor: $clickable ? 'pointer' : 'default',
    userSelect: 'none',

    ...text(g.labelRole),
    lineHeight: 1,

    backgroundColor: p.bg,
    color: p.fg,
    border: `1px solid ${$variant === 'outline' ? p.border : 'transparent'}`,
    transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
      duration: theme.transitions.duration.shorter,
    }),

    '&:hover': $clickable ? {backgroundColor: p.bgHover} : undefined,
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.55,
    },
  };
});

// Remove button — `<button>` element for proper keyboard activation.
// Hover paints a circular currentColor tint via ::before so the icon
// itself stays at full opacity. currentColor inherits the Chip's fg →
// light text on solid chip → light tint; dark text on soft → dark tint.
const RemoveButton = styled('button', {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$size: ChipSize}>(({theme, $size}) => {
  const px = GEO[$size].removePx + 4;
  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: px,
    height: px,
    padding: 0,
    border: 0,
    background: 'transparent',
    borderRadius: '50%',
    color: 'inherit',
    cursor: 'pointer',
    opacity: 0.75,
    transition: theme.transitions.create(['opacity'], {
      duration: theme.transitions.duration.shorter,
    }),
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: '50%',
      backgroundColor: 'currentColor',
      opacity: 0,
      transition: theme.transitions.create(['opacity'], {
        duration: theme.transitions.duration.shorter,
      }),
      pointerEvents: 'none',
    },
    '&:hover': {opacity: 1},
    '&:hover::before': {opacity: 0.2},
    '&:active::before': {opacity: 0.3},
    '&:focus-visible': {
      outline: '2px solid currentColor',
      outlineOffset: 1,
      opacity: 1,
    },
  };
});

export interface ChipProps {
  children?: ReactNode;
  leftIcon?: string | ReactNode;
  selected?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  disabled?: boolean;
  size?: ChipSize;
  tone?: ChipTone;
  variant?: ChipVariant;
  ariaLabel?: string;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
}

function renderIcon(icon: string | ReactNode | undefined, px: number): ReactNode {
  if (!icon) return null;
  if (typeof icon === 'string') return <Icon name={icon} size={px} color='inherit' />;
  if (isValidElement(icon)) return icon;
  return icon;
}

export const Chip = forwardRef<HTMLButtonElement, ChipProps>(function Chip(
  {
    children,
    leftIcon,
    selected = false,
    onClick,
    onRemove,
    disabled = false,
    size = 's',
    tone = 'primary',
    variant = 'soft',
    ariaLabel,
    className,
    type = 'button',
  },
  ref,
) {
  const g = GEO[size];
  const clickable = Boolean(onClick) && !disabled;
  const hasRemove = Boolean(onRemove);

  const handleRemove = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!disabled) onRemove?.();
  };

  return (
    <Root
      ref={ref}
      type={type}
      role={clickable ? undefined : 'group'}
      aria-pressed={selected || undefined}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={clickable ? onClick : undefined}
      $size={size}
      $variant={variant}
      $tone={tone}
      $selected={selected}
      $clickable={clickable}
      $hasRemove={hasRemove}
      $hasLeftIcon={Boolean(leftIcon)}
      className={className}
    >
      {renderIcon(leftIcon, iconPxForRole(g.labelRole))}
      {children != null && <Box component='span'>{children}</Box>}
      {hasRemove && (
        <RemoveButton
          $size={size}
          type='button'
          aria-label='Remove'
          onClick={handleRemove}
          disabled={disabled}
        >
          <Icon name='close' size={g.removePx} color='inherit' />
        </RemoveButton>
      )}
    </Root>
  );
});
